import { backend } from '@/services/backend'
import type { CustomList, UserDocListItem, WaitUI } from './interfaces'
import { resetCustomListsCache, updateDocsUserDataCache } from '@/helpers/cache'
import { Mixpanel } from '@/analytics/mixpanel'
import { Sentry } from '@/services/sentry'
import type { DocsView } from './doc'
import { setDocFavorite } from './doc.favorite'
import { checklist } from './checklist'

/**
 * A destination in the Save destination menu: Favorites or a list.
 */
type SaveDestination = 'favorites' | CustomList

/**
 * Return `true` if the given `list` is "Want to Read".
 */
export function isWantToRead(list: CustomList): boolean {
  return list.url_slug === 'want-to-read'
}

/**
 * Follow-up actions after adding / removing the doc from "Want to Read".
 *
 * This sends a Mixpanel event for adding/removing the doc to Want to Read
 * and, if it was added, counts it towards the onboarding checklist item that
 * tracks whether a user saved 3 items to Want to Read.
 *
 * @param doc - The doc that was added / removed.
 * @param docCustomLists - The doc's Custom Lists after the change.
 * @param shouldBeInWantToRead - `true` if the doc was added, `false` if it
 *   was removed.
 */
export function afterWantToReadChange(
  doc: UserDocListItem,
  docCustomLists: CustomList[],
  shouldBeInWantToRead: boolean,
): void {
  Mixpanel.trackWantToRead(doc, docCustomLists, shouldBeInWantToRead)

  if (shouldBeInWantToRead) {
    checklist()?.steps.save_three.addProgress(1)
  }
}

/**
 * Logic related to the Custom Lists of a given `doc`.
 */
export class DocCustomListsView {
  doc: UserDocListItem
  private docsView?: DocsView
  private waitHandler: WaitUI
  private updateListsDelay: number | null = null
  // Favorites, Want to Read, and Custom Lists requests, run one after another
  // so that each sees the doc as updated by the previous one.
  private listRequests: Promise<void> = Promise.resolve()

  lists: CustomList[] = []
  listsState: Record<string, boolean> = {}

  constructor(doc: UserDocListItem, waitHandler: WaitUI, docsView?: DocsView) {
    this.doc = doc
    this.waitHandler = waitHandler
    this.docsView = docsView
  }

  get isInWantToRead(): boolean {
    return this.doc.user.custom_lists.some(isWantToRead)
  }

  /**
   * Return `true` if the doc is one or more lists, or marked as Favorite.
   */
  get isDocInAnyList(): boolean {
    return this.doc.user.is_favorite || this.doc.user.custom_lists.length > 0
  }

  /**
   * Return `true` if the doc is in the given `list`.
   */
  isDocInList(list: CustomList): boolean {
    return this.doc.user.custom_lists.some((docList) => list.id === docList.id)
  }

  /**
   * Load the user's lists, and marks which ones contain the doc.
   */
  async getLists(): Promise<void> {
    this.waitHandler.end('Error Loading Lists')
    this.waitHandler.start('Loading Lists')

    try {
      this.lists = await backend.getCustomLists()
      this.lists.forEach((list) => {
        this.listsState[list.url_slug] = this.isDocInList(list)
      })
    } catch (error) {
      this.waitHandler.start('Error Loading Lists')

      this.lists = []
      this.listsState = {}

      Sentry.captureException(error)
    }

    this.waitHandler.end('Loading Lists')
  }

  /**
   * Add / Remove the doc from the "Want to Read" list.
   */
  async toggleWantToRead(): Promise<void> {
    await this.setInWantToRead(!this.isInWantToRead)
  }

  /**
   * Set Want to Read membership to the requested state.
   */
  async setInWantToRead(shouldBeInWantToRead: boolean): Promise<void> {
    if (this.isInWantToRead === shouldBeInWantToRead) {
      return
    }

    const result = shouldBeInWantToRead
      ? await backend.addToWantToRead(this.doc)
      : await backend.removeFromWantToRead(this.doc)

    afterWantToReadChange(this.doc, result.custom_lists, shouldBeInWantToRead)

    await this.updateDoc(result.custom_lists)

    // Reset cache to force fresh data loading.
    // Otherwise, we mark the book as favorite on the books page, for example,
    // then open the home page and the book is not marked as favorite (additional
    // page refresh is needed to see the favorite mark).
    await resetCustomListsCache(this.doc.doc_type)
  }

  /**
   * Apply the desired membership for a Save destination.
   * Favorites and Want to Read update immediately; custom lists go through
   * `toggleList()` and its shared tracking and cache updates.
   */
  async toggleDestination(
    destination: SaveDestination,
    checked: boolean,
  ): Promise<void> {
    if (destination === 'favorites') {
      await this.runAfterPreviousRequests(() =>
        setDocFavorite(this.doc, checked, this.docsView),
      )
    } else if (isWantToRead(destination)) {
      // Keep listsState in sync. If we don't, then if `updateListsBackend()`
      // gets called later, it would send listsState without this membership
      // and revert the doc being added to Want to Read.
      this.listsState[destination.url_slug] = checked
      await this.runAfterPreviousRequests(() => this.setInWantToRead(checked))
    } else {
      await this.toggleList(destination, checked)
    }
  }

  /**
   * Run `request` once the previous Favorites / Want to Read / Custom Lists
   * requests finish.
   *
   * The caller passes in a promise that runs the favorite/Want to Read setter,
   * or `updateListsBackend()`. (This is `request`.)
   *
   * We add `request` to the end of the request queue (`listRequests`).
   * Using `.then()` means that all requests that are already in
   * `listRequests` must run before the `request` we are newly adding.
   *
   * Example where there are 3 clicks and none of the promises return until the
   * 3rd click is done:
   *
   * Initialization
   *   -> this.listRequests = Promise.resolve() = promise0
   * Click 1: check Favorites (request1)
   *   -> this.listRequests = promise0.then(request1) = promise1
   * Click 2: uncheck Favorites (request2)
   *   -> this.listRequests = promise1.then(request2)
   *      = promise0.then(request1).then(request2)
   *      = promise2
   * Click 3: check Want to Read (request3)
   *   -> this.listRequests = promise2.then(request3)
   *      = promise0.then(request1).then(request2).then(request3)
   *      = promise3
   * request1 finishes, so promise1 resolves
   *   -> request2 and request3 remain
   * request2 finishes, so promise2 resolves
   *   -> request3 remains
   * request3 finishes, so promise3 resolves
   *   -> all requests have finished.
   *      (listRequests is still promise3, but since it has resolved, the
   *      next call will immediately start the next `request`.)
   *
   * Without this, checking then quickly unchecking may send only the first
   * request:
   *
   * time 0: setDocFavorite(true)
   *   -> is not favorited yet, so it sends the request and waits for the
   *      response (which does not arrive until time 2)
   * time 1: setDocFavorite(false)
   *   -> since it is not favorited yet, it is the "correct" state already, so
   *      the setter skips sending a un-favorite request
   * time 2: first request finishes, and the doc is favorited
   *
   * In this example, the doc is favorited even though the final state should
   * be unfavorited.
   */
  private runAfterPreviousRequests(
    request: () => Promise<void>,
  ): Promise<void> {
    this.listRequests = this.listRequests.then(request).catch((error) => {
      Sentry.captureException(error)
    })
    return this.listRequests
  }

  /**
   * Adds / removes the doc from the given `list` based on the `updatedValue` value.
   *
   * This function has a 500ms debounce to prevent creating too many requests when the
   * user's toggling many lists at once.
   */
  async toggleList(
    list: CustomList,
    updatedValue: boolean,
    skipDebounce: boolean = false,
  ): Promise<void> {
    this.listsState[list.url_slug] = updatedValue

    if (skipDebounce) {
      await this.runAfterPreviousRequests(() => this.updateListsBackend())
      return
    }

    if (this.updateListsDelay) {
      clearTimeout(this.updateListsDelay)
    }

    this.updateListsDelay = setTimeout(() => {
      this.runAfterPreviousRequests(() => this.updateListsBackend())
    }, 500)
  }

  /**
   * Sends a backend request to update the doc's lists based on `this.listsState`.
   *
   * @param updateView - Whether or not to update the view. By default, we only update
   *   it when the user closes the popup. This way it won't abruptly remove the doc they
   *   were interacting with.
   */
  async updateListsBackend(updateView: boolean = false): Promise<void> {
    try {
      const before_lists = [...this.doc.user.custom_lists]
      const wasInWantToRead = this.isInWantToRead
      const result = await backend.updateCustomLists(this.doc, this.listsState)

      const before_ids_set = new Set(before_lists.map((ls) => ls.id))
      const result_ids_set = new Set(result.custom_lists.map((ls) => ls.id))
      // Exclude "Want to Read" from being counted as a removed or added list
      // here; only custom lists count.
      const removed_list = before_lists.filter(
        (ls) => !result_ids_set.has(ls.id) && !isWantToRead(ls),
      )
      const added_list = result.custom_lists.filter(
        (ls) => !before_ids_set.has(ls.id) && !isWantToRead(ls),
      )

      // Instead of treating Want to Read as a "custom" list above, handle it
      // as a special case.
      const isNowInWantToRead = result.custom_lists.some(isWantToRead)
      if (wasInWantToRead !== isNowInWantToRead) {
        afterWantToReadChange(this.doc, result.custom_lists, isNowInWantToRead)
      }
      if (removed_list.length) {
        Mixpanel.trackRemoveFromCustomList(this.doc, removed_list)
      }
      if (added_list.length) {
        Mixpanel.trackAddToCustomList(this.doc, added_list)
      }

      await this.updateDoc(result.custom_lists, updateView)

      // Reset cache to force fresh data loading.
      // Otherwise, we mark the book as favorite on the books page, for example,
      // then open the home page and the book is not marked as favorite (additional
      // page refresh is needed to see the favorite mark).
      await resetCustomListsCache(this.doc.doc_type)
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  /**
   * Update the doc or docsView.
   *
   * @param newLists - The updated array of custom lists for the doc.
   * @param updateView - Whether or not to update the view. It's useful
   *   to have control over this to avoid the view updating and removing
   *   the doc while the user is still interacting with the lists selection popup.
   */
  async updateDoc(
    newLists: CustomList[],
    updateView: boolean = true,
  ): Promise<void> {
    if (this.docsView && updateView) {
      await this.docsView.updateDocCustomLists(this.doc, newLists)
    } else {
      this.doc.user.custom_lists = newLists
    }
    await updateDocsUserDataCache(this.doc.id, this.doc.user)
  }

  /**
   * Clear any remaining timeouts, `lists`, and `listsState` data to preserve
   * memory usage.
   */
  async clearState(): Promise<void> {
    if (this.updateListsDelay) {
      clearTimeout(this.updateListsDelay)
      // Run one last updateListsBackend() with updateView = true. This forces
      // `docsView` (if any) to update its filtered listing of docs and reflect
      // the changed list membership(s).
      await this.runAfterPreviousRequests(() => this.updateListsBackend(true))
    }

    this.lists = []
    this.listsState = {}
  }
}
